import { apiFetch, attendreTache, EN_TETE_TACHE_ASYNCHRONE } from './api';

/**
 * Fiches formateurs (menu Formateurs). Une fiche s'assigne à une journée
 * planifiée ; nom et email sont alors recopiés sur la journée, et le
 * formateur reçoit sa convocation, ses rappels et son lien d'émargement.
 */

/** Les paramètres vides ne sont pas transmis : le serveur applique ses défauts. */
function versQuery(params) {
  const query = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') query.set(cle, valeur);
  }
  const s = query.toString();
  return s ? `?${s}` : '';
}

/** Liste ; `{ actif: true }` pour le sélecteur de la planification. */
export function getFormateurs(params = {}) {
  return apiFetch(`/formateurs${versQuery(params)}`);
}

/** Fiche complète : journées assignées, documents produits, émargements. */
export function getFormateurDossier(id) {
  return apiFetch(`/formateurs/${id}`);
}

/** Les journées planifiées qui ont un intervenant (menu Contrats des formateurs). */
export function getContratsFormateurs() {
  return apiFetch('/formateurs/contrats');
}

export function createFormateur(body) {
  return apiFetch('/formateurs', { method: 'POST', body: JSON.stringify(body) });
}

export function updateFormateur(id, body) {
  return apiFetch(`/formateurs/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}

export function deleteFormateur(id) {
  return apiFetch(`/formateurs/${id}`, { method: 'DELETE' });
}

/**
 * Les résultats d'évaluation des stagiaires de ses séances : { global, seances }
 * — réussite et note moyennes, invités et répondants, détail par stagiaire.
 */
export function getEvaluationsFormateur(id) {
  return apiFetch(`/formateurs/${id}/evaluations`);
}

/** Crée le compte de connexion du formateur (mot de passe par défaut). */
export function creerAccesFormateur(id) {
  return apiFetch(`/formateurs/${id}/acces`, { method: 'POST' });
}

/** Remet le mot de passe par défaut (mot de passe oublié). */
export function reinitialiserAccesFormateur(id) {
  return apiFetch(`/formateurs/${id}/acces/reinitialiser`, { method: 'POST' });
}

/* ---------------- Pièces administratives (migration 080) ---------------- */

const API_URL = process.env.REACT_APP_API_URL || '/api';

export function getPiecesFormateur(formateurId) {
  return apiFetch(`/formateurs/${formateurId}/pieces`);
}

/** Dépôt multipart (hors apiFetch, qui force le JSON) : type, échéance, fichier. */
export async function deposerPieceFormateur(formateurId, { type, dateEcheance, libelle, fichier }) {
  const corps = new FormData();
  corps.append('type', type);
  if (dateEcheance) corps.append('dateEcheance', dateEcheance);
  if (libelle) corps.append('libelle', libelle);
  corps.append('fichier', fichier);
  const res = await fetch(`${API_URL}/formateurs/${formateurId}/pieces`, {
    method: 'POST',
    credentials: 'include',
    body: corps,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json?.message || `Dépôt impossible (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json;
}

/** Refait le CV anonyme à partir du dernier CV déposé (lecture par l'IA). */
export function genererCvAnonyme(formateurId) {
  return apiFetch(`/formateurs/${formateurId}/cv-anonyme`, { method: 'POST' });
}

export function modifierPieceFormateur(pieceId, body) {
  return apiFetch(`/formateurs/pieces/${pieceId}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export function supprimerPieceFormateur(pieceId) {
  return apiFetch(`/formateurs/pieces/${pieceId}`, { method: 'DELETE' });
}

export function urlPieceFormateur(pieceId, { apercu = false } = {}) {
  return `${API_URL}/formateurs/pieces/${pieceId}/fichier${apercu ? '?apercu=1' : ''}`;
}

/**
 * Remplir la fiche avec l'IA : le texte des notes internes et les pièces
 * choisies (CV, attestations…). Renvoie une proposition { data, pieces }.
 */
export async function extraireFormateur({ texte = '', pieces = [] }) {
  const corps = new FormData();
  corps.append('texte', texte || '');
  for (const { type, fichier } of pieces) {
    corps.append('fichiers', fichier);
    corps.append('types', type);
  }
  const res = await fetch(`${API_URL}/formateurs/extraction`, {
    method: 'POST',
    credentials: 'include',
    headers: EN_TETE_TACHE_ASYNCHRONE,
    body: corps,
  });
  const json = await res.json().catch(() => ({}));
  // Lancé en tâche de fond : on attend son résultat (même forme).
  if (res.status === 202 && json.tacheId) return attendreTache(json.tacheId);
  if (!res.ok) {
    const err = new Error(json?.message || `Lecture impossible (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json;
}

/**
 * Messagerie : un message à plusieurs formateurs. `@mail` et `@motdepasse`
 * sont remplacés, pour chacun, par son adresse de connexion et son mot de passe.
 * `apercu` montre ce que recevrait chaque formateur ; rien n'est envoyé.
 */
export function apercuMessageFormateurs({ formateurIds, sujet, texte }) {
  return apiFetch('/formateurs/messagerie/apercu', {
    method: 'POST',
    body: JSON.stringify({ formateurIds, sujet, texte }),
  });
}

export function envoyerMessageFormateurs({ formateurIds, sujet, texte }) {
  return apiFetch('/formateurs/messagerie/envoyer', {
    method: 'POST',
    body: JSON.stringify({ formateurIds, sujet, texte }),
  });
}
